import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { apiRequest } from '../utils/api';
import { isLightAuthPath, userDetailsEndpoint } from '../utils/authPaths';
import { clearAnalysisFilters } from '../components2/analysis/utils/analysisFilters';
import { useChatState } from '../contexts/ChatStateContext';

const AuthContext2 = createContext();

export const useAuth2 = () => {
  const context = useContext(AuthContext2);
  if (context === undefined) {
    throw new Error('useAuth2 must be used within an AuthProvider2');
  }
  return context;
};

export function AuthProvider2({ children }) {
  // 🔍 RENDER TRACKING - Use ref to avoid infinite loops
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  //console.log(`🔄 [AuthContext2] Provider render #${renderCountRef.current}`);

  // 🎯 CHAT STATE CONTEXT - For updating chat state from auth responses
  const { updateFromBackend } = useChatState();

  // 🎯 MINIMAL AUTH STATE - Only what we need for clean architecture
  const [authState, setAuthState] = useState({
    isChecking: true,      // Are we checking auth status?
    isAuthenticated: false, // Is user authenticated?
    user: null,            // User data (email, subscription, etc.)
    error: null,           // Auth error if any
    isLoggingOut: false    // Are we in the process of logging out?
  });

  // 🔐 CHECK AUTHENTICATION - Single, clean check
  useEffect(() => {
    const checkAuthentication = async () => {
      // Skip auth check for truly public routes (only Matrix)
      const publicRoutes = ['/matrix'];
      const currentPath = window.location.pathname;
      
      if (publicRoutes.includes(currentPath)) {
        //console.log('🔄 [AuthContext2] Skipping auth check for public route:', currentPath);
        setAuthState({
          isChecking: false,
          isAuthenticated: false,
          user: null,
          error: null
        });
        return;
      }
      
      // Skip initial auth check if we're in an OAuth callback flow
      // AuthCallback component will provide auth data via event
      const urlParams = new URLSearchParams(window.location.search);
      const isOAuthCallback = urlParams.has('code') && window.location.pathname === '/auth-callback';
      
      if (isOAuthCallback) {
        //console.log('🔄 [AuthContext2] Skipping initial auth check - OAuth callback in progress');
        // Set checking to false immediately so app doesn't get stuck
        setAuthState(prev => ({
          ...prev,
          isChecking: false
        }));
        
        // Safety net: If no auth event is received within 10 seconds, fall back to normal auth check
        const safetyTimeout = setTimeout(async () => {
          //console.log('⚠️ [AuthContext2] OAuth callback timeout - falling back to auth check');
          try {
            const isVerifyPath = isLightAuthPath(window.location.pathname);
            const userEndpoint = userDetailsEndpoint(window.location.pathname);

            const response = await apiRequest(userEndpoint);
            if (response.ok) {
              const userData = await response.json();

              // 🎯 UPDATE CHAT STATE: Safety timeout auth check also provides current chat state (main app only)
              if (userData.chat_state && !isVerifyPath) {
                console.log('🎯 [AuthContext2] Updating chat state from safety timeout auth:', userData.chat_state);
                updateFromBackend(userData.chat_state);
              }
              
              setAuthState({
                isChecking: false,
                isAuthenticated: true,
                user: userData,
                error: null
              });
            } else {
              setAuthState({
                isChecking: false,
                isAuthenticated: false,
                user: null,
                error: null
              });
            }
          } catch (error) {
            setAuthState({
              isChecking: false,
              isAuthenticated: false,
              user: null,
              error: error.message
            });
          }
        }, 10000);
        
        // Store timeout ID so auth event listener can clear it
        window.authCallbackTimeout = safetyTimeout;
        return;
      }

      // 🎯 OPTIMIZATION: Fast path for completely new users
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        //console.log('🔄 [AuthContext2] No access token found - user is definitely not authenticated');
        setAuthState({
          isChecking: false,
          isAuthenticated: false,
          user: null,
          error: null
        });
        return; // 🚀 SKIP API CALLS ENTIRELY - saves ~1000ms for new users
      }

      //console.log('🔐 [AuthContext2] Starting authentication check...');
      
      try {
        const isVerifyPath = isLightAuthPath(window.location.pathname);
        const userEndpoint = userDetailsEndpoint(window.location.pathname);
        
        const response = await apiRequest(userEndpoint);
        
        if (response.ok) {
          const userData = await response.json();
          //console.log('✅ [AuthContext2] Authentication successful:', userData.email);
          
          // 🎯 UPDATE CHAT STATE: Auth refresh endpoint provides current chat state (main app only)
          if (userData.chat_state && !isVerifyPath) {
            console.log('🎯 [AuthContext2] Updating chat state from auth refresh:', userData.chat_state);
            updateFromBackend(userData.chat_state);
          } else if (!isVerifyPath) {
            console.log('⚠️ [AuthContext2] No chat_state in auth response');
          }
          
          setAuthState({
            isChecking: false,
            isAuthenticated: true,
            user: userData,
            error: null
          });
        } else {
          //console.log('❌ [AuthContext2] Not authenticated');
          setAuthState({
            isChecking: false,
            isAuthenticated: false,
            user: null,
            error: null
          });
        }
      } catch (error) {
        //console.error('❌ [AuthContext2] Auth check failed:', error);
        setAuthState({
          isChecking: false,
          isAuthenticated: false,
          user: null,
          error: error.message
        });
      }
    };

    checkAuthentication();
  }, []); // Only run once on mount

  // 🔄 LISTEN FOR AUTH UPDATES - Handle auth-status-update events from OAuth callback
  useEffect(() => {
    const handleAuthUpdate = (event) => {
      //console.log('🔄 [AuthContext2] Received auth-status-update event:', event.detail);
      
      // Clear safety timeout if it exists
      if (window.authCallbackTimeout) {
        clearTimeout(window.authCallbackTimeout);
        window.authCallbackTimeout = null;
      }
      
      if (event.detail && event.detail.isAuthenticated) {
        setAuthState({
          isChecking: false,
          isAuthenticated: true,
          user: {
            email: event.detail.email,
            id: event.detail.userId,
            subscription_tier: event.detail.subscriptionTier,
            is_demo_user: event.detail.isDemoUser,
            subscription_limits: event.detail.subscriptionLimits || {},
            onboarding_completed: event.detail.onboardingCompleted || false,
            app_data: event.detail.appData // Include app data from auth event
          },
          error: null
        });
      }
    };

    window.addEventListener('auth-status-update', handleAuthUpdate);
    
    return () => {
      window.removeEventListener('auth-status-update', handleAuthUpdate);
      // Clean up timeout on unmount
      if (window.authCallbackTimeout) {
        clearTimeout(window.authCallbackTimeout);
        window.authCallbackTimeout = null;
      }
    };
  }, []);

  // 🚪 LOGOUT FUNCTION
  const logout = async () => {
    //console.log('🚪 [AuthContext2] Logging out...'); 
    
    // Set logging out state immediately for better UX
    setAuthState(prev => ({
      ...prev,
      isLoggingOut: true
    }));
    
    try {
      await apiRequest('/api/auth/logout/', { method: 'POST' });
    } catch (error) {
      //console.error('❌ [AuthContext2] Logout error:', error);
    }
      
    // Clear access token from localStorage to prevent auto-login on refresh
    localStorage.removeItem('accessToken');
    clearAnalysisFilters();

    //console.log('🧹 [AuthContext2] Cleared access token from localStorage');
    
    // Always clear auth state regardless of API success
    setAuthState({
      isChecking: false,
      isAuthenticated: false,
      user: null,
      error: null,
      isLoggingOut: false
    });
  };

  // 🔄 SET AUTH STATUS (for login flows)
  const setAuthStatus = (newAuthState) => {
    //console.log('🔄 [AuthContext2] Setting auth status:', newAuthState);
    setAuthState(prev => ({
      ...prev,
      ...newAuthState
    }));
  };

      const contextValue = {
      // State
      authStatus: {
        ...authState,
        // Extract commonly used user fields for convenience
        email: authState.user?.email || '',
        userId: authState.user?.id || null,
        subscriptionTier: authState.user?.subscription_tier || 'none',
        isDemoUser: authState.user?.is_demo_user || false,
        subscriptionLimits: authState.user?.subscription_limits || {},
        // Extract onboarding fields for convenience
        onboarding_completed: authState.user?.onboarding_completed || false,
        onboarding_completed_at: authState.user?.onboarding_completed_at || null
      },
    
    // Actions  
    logout,
    setAuthStatus,
    
    // Convenience getters
    isAuthenticated: authState.isAuthenticated,
    isChecking: authState.isChecking,
    user: authState.user,
    error: authState.error
  };

  return (
    <AuthContext2.Provider value={contextValue}>
      {children}
    </AuthContext2.Provider>
  );
} 